import React, { useState, useEffect } from 'react';
import BaseButton from '../../Common/BaseButton';
import { useEmailStyles } from '../../../components/Context/EmailStyleContext';
import Skeleton from 'react-loading-skeleton';

const EmailTextEditor = ({
  content,
  onContentChange,
  onRegenerate = null,
  allowRegenerate = false,
  isLoading = false,
  type = 'text', // 'text', 'heading'
  label = '',
  placeholder = 'Enter text here',
  rows = 3,
  previewMode = false,
  className = ''
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localContent, setLocalContent] = useState(content || '');
  const { styles } = useEmailStyles();
  
  // Log styles when they change for debugging
  useEffect(() => {
    console.log('EmailTextEditor: Current styles', styles);
  }, [styles]);
  
  useEffect(() => {
    if (content !== localContent && !isEditing) {
      setLocalContent(content);
    }
  }, [content, isEditing, localContent]);
  
  const handleEditClick = () => {
    setIsEditing(true);
  };
  
  const handleDoneClick = () => {
    setIsEditing(false);
    if (onContentChange) {
      onContentChange(localContent);
    }
  };
  
  const handleRegenerate = () => {
    if (onRegenerate) {
      onRegenerate();
    }
  };
  
  const handleChange = (e) => {
    setLocalContent(e.target.value);
  };
  
  // Apply appropriate styling based on type with fallbacks
  const displayStyles = type === 'heading' 
    ? { 
        fontFamily: styles?.fontFamily || 'helvetica, "helvetica neue", arial, verdana, sans-serif', 
        color: styles?.secondaryColor || '#a1894c' 
      }
    : { 
        fontFamily: styles?.fontFamily || 'helvetica, "helvetica neue", arial, verdana, sans-serif', 
        color: styles?.primaryColor || '#333333' 
      };
  
  if (previewMode) {
    // In preview mode, just display the content with styling
    return type === 'heading' ? (
      <h3 style={displayStyles} className={className}>{localContent}</h3>
    ) : (
      <p style={displayStyles} className={`description-text ${className}`}>{localContent}</p>
    );
  }
  
  if (isLoading) {
    return <Skeleton height={type === 'heading' ? 40 : 80} width="100%" />;
  }
  
  return (
    <div className={`email-text-editor-container ${className}`}>
      {label && <label className="form-label"><strong>{label}</strong></label>}
      
      {!isEditing ? (
        <>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="styled-content">
              {type === 'heading' ? (
                <h3 style={displayStyles}>{localContent}</h3>
              ) : (
                <p style={displayStyles}>{localContent}</p>
              )}
            </div>
            <div className="button-group">
              {allowRegenerate && (
                <BaseButton
                  className="outlineprimary"
                  children="Regenerate Text"
                  imageSrc="/regenerate-text.svg"
                  handleClick={handleRegenerate}
                />
              )}
              <BaseButton
                className="outlineprimary"
                children="Edit Text"
                imageSrc="/edit-text.svg"
                handleClick={handleEditClick}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="mt-2">
          {type === 'heading' ? (
            <input
              type="text"
              className="form-control mb-2"
              value={localContent}
              onChange={handleChange}
              placeholder={placeholder}
            />
          ) : (
            <textarea
              className="form-control mb-2"
              rows={rows}
              value={localContent}
              onChange={handleChange}
              placeholder={placeholder}
            />
          )}
          <button className="btn btn-dark btn-sm" onClick={handleDoneClick}>
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailTextEditor; 